import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardList, UtensilsCrossed, Heart, BookOpen, AlertTriangle,
  BarChart3, Users, GraduationCap, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KPICard({ label, labelAlt, value, icon: Icon, color, trend, onClick }) {
  return (
    <div className="stat-card cursor-pointer animate-reveal" onClick={onClick}
      data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-value" style={{ color: color || 'var(--color-label-primary)' }}>{value}</p>
          <div className="bi-label" style={{ marginTop: 'var(--space-1)' }}>
            <span className="stat-label">{label}</span>
            {labelAlt && <span style={{ fontSize: '11px', opacity: 0.4, fontWeight: 400, marginTop: 2 }}>{labelAlt}</span>}
          </div>
        </div>
        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={20} style={{ color: color || 'var(--color-accent)' }} />
        </div>
      </div>
      {trend && (
        <div className="stat-trend" style={{ color: trend > 0 ? 'var(--color-success)' : trend < 0 ? 'var(--color-error)' : 'var(--color-label-tertiary)' }}>
          {trend > 0 ? <TrendingUp size={13} /> : trend < 0 ? <TrendingDown size={13} /> : <Minus size={13} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );
}

function TeacherDashboard() {
  const { t, lang, currentSchool, currentTeacher } = useApp();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('greeting_morning') : hour < 17 ? t('greeting_afternoon') : t('greeting_evening');

  useEffect(() => {
    if (currentSchool) {
      axios.get(`${API}/attendance/report`, { params: { school_id: currentSchool.school_id } })
        .then(r => setReport(r.data)).catch(() => {});
    }
  }, [currentSchool]);

  const quickActions = [
    { en: 'Mark Attendance', bn: 'হাজিরা নিন', icon: ClipboardList, path: '/attendance' },
    { en: 'Record Meal', bn: 'খাবারের তথ্য', icon: UtensilsCrossed, path: '/meals' },
    { en: 'My Health', bn: 'আমার স্বাস্থ্য', icon: Heart, path: '/health' },
    { en: 'My Learning', bn: 'আমার শিখন', icon: BookOpen, path: '/lms' },
    { en: 'Grades', bn: 'ফলাফল', icon: GraduationCap, path: '/grades' },
    { en: 'Communication', bn: 'যোগাযোগ', icon: AlertTriangle, path: '/communication' },
  ];

  return (
    <div className="space-y-6" data-testid="teacher-dashboard" data-reveal-stagger="true">
      {/* Hero Greeting */}
      <div style={{ paddingTop: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-title1)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
          {greeting}, {lang === 'bn' ? (currentTeacher?.name_bangla || '') : (currentTeacher?.name_english || currentTeacher?.name_bangla || '')}
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--color-label-tertiary)', marginTop: 4, fontWeight: 400 }}>
          {lang === 'bn' ? (currentTeacher?.name_english || '') : (currentTeacher?.name_bangla || '')}
        </p>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }}>
            {lang === 'bn' ? (currentSchool?.name_bangla || '') : (currentSchool?.name_english || currentSchool?.name_bangla || '')}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
            {lang === 'bn' ? (currentSchool?.name_english || '') : (currentSchool?.name_bangla || '')}
          </p>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--text-caption)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-red)' }}>
            {lang === 'bn' ? 'দ্রুত কাজ' : 'Quick Actions'}
          </h3>
          <p style={{ fontSize: '10px', color: 'var(--color-label-tertiary)', marginTop: 1 }}>
            {lang === 'bn' ? 'Quick Actions' : 'দ্রুত কাজ'}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3" data-reveal-stagger="true">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <button key={action.path} data-testid={`quick-action-${action.path.replace('/', '')}`}
                onClick={() => navigate(action.path)}
                className="card-surface text-left animate-reveal" style={{ padding: 'var(--space-5)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
                  <Icon size={20} style={{ color: 'var(--color-accent)' }} />
                </div>
                <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-primary)' }}>
                  {lang === 'bn' ? action.bn : action.en}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 3, fontWeight: 400 }}>
                  {lang === 'bn' ? action.en : action.bn}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Attendance Report */}
      {report && (
        <div className="card-surface animate-reveal">
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'হাজিরার প্রতিবেদন' : 'Attendance Report'}</h3>
            <p style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Attendance Report' : 'হাজিরার প্রতিবেদন'}</p>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div style={{ fontSize: '40px', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--color-accent)' }}>{report.attendance_rate}%</div>
            <div style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }} className="font-bangla">{t('att_rate')}</div>
          </div>
          {report.daily?.length > 0 && (
            <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={120}>
              <BarChart data={report.daily.slice(-7)}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-separator)" />
                <XAxis dataKey="date" tickFormatter={d => d.slice(8)} tick={{ fontSize: 10, fill: 'var(--color-label-tertiary)' }} />
                <YAxis hide />
                <Tooltip contentStyle={{ background: 'white', boxShadow: 'var(--shadow-lg)', borderRadius: 'var(--radius-md)', border: 'none', fontSize: 14 }} />
                <Bar dataKey="present" fill="var(--color-accent)" radius={[4,4,0,0]} />
                <Bar dataKey="absent" fill="var(--color-error)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Sync Status */}
      <div className="card-flat flex items-center justify-between animate-reveal">
        <div className="flex items-center gap-2">
          <span className="status-dot status-connected" />
          <span className="font-bangla" style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)' }}>{t('sync_online')}</span>
        </div>
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)' }}>{t('sync_pending_items')}: 5</span>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { t, lang, role } = useApp();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/governance/dashboard`, { params: { role } })
      .then(r => { setDashboard(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [role]);

  if (loading) return <div className="text-center py-12 text-slate-400">{t('loading')}</div>;
  if (!dashboard) return null;

  // Handle both old ({summary:{...}, schools, alerts}) and new (flat) API response shapes
  const summary = dashboard.summary || dashboard;
  const schools = dashboard.schools || [];
  const alerts = dashboard.alerts || [];

  const attData = [
    { name: 'Present', value: summary.attendance_rate || 0, color: 'var(--color-accent)' },
    { name: 'Absent', value: 100 - (summary.attendance_rate || 0), color: 'var(--color-surface-3)' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard" data-reveal-stagger="true">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard label={lang === 'bn' ? 'মোট বিদ্যালয়' : 'Total Schools'} labelAlt={lang === 'bn' ? 'Total Schools' : 'মোট বিদ্যালয়'} value={summary.total_schools || 0} icon={BarChart3} />
        <KPICard label={lang === 'bn' ? 'মোট শিক্ষার্থী' : 'Total Students'} labelAlt={lang === 'bn' ? 'Total Students' : 'মোট শিক্ষার্থী'} value={(summary.total_students || 0).toLocaleString()} icon={GraduationCap} color="var(--color-accent)" trend={2.3} />
        <KPICard label={lang === 'bn' ? 'উপস্থিতির হার' : 'Attendance Rate'} labelAlt={lang === 'bn' ? 'Attendance Rate' : 'উপস্থিতির হার'} value={`${summary.attendance_rate || 0}%`} icon={ClipboardList} color="var(--color-accent)" trend={1.5} />
        <KPICard label={lang === 'bn' ? 'খাবার সম্মতি' : 'Meal Compliance'} labelAlt={lang === 'bn' ? 'Meal Compliance' : 'খাবার সম্মতি'} value={`${summary.meal_compliance || summary.lms_compliance || 0}%`} icon={UtensilsCrossed} color="var(--color-warning)" trend={-0.8} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPICard label={lang === 'bn' ? 'মোট শিক্ষক' : 'Total Teachers'} labelAlt={lang === 'bn' ? 'Total Teachers' : 'মোট শিক্ষক'} value={summary.total_teachers || 0} icon={Users} />
        <KPICard label={lang === 'bn' ? 'মোট পরিবেশিত' : 'Meals Served'} labelAlt={lang === 'bn' ? 'Meals Served' : 'মোট পরিবেশিত'} value={(summary.total_meals_served || 0).toLocaleString()} icon={UtensilsCrossed} color="var(--color-success)" />
        <KPICard label={lang === 'bn' ? 'অসঙ্গতি' : 'Anomalies'} labelAlt={lang === 'bn' ? 'Anomalies' : 'অসঙ্গতি'} value={summary.meal_anomalies || 0} icon={AlertTriangle} color="var(--color-error)" onClick={() => navigate('/meals')} />
        <KPICard label={lang === 'bn' ? 'কল্যাণ সতর্কতা' : 'Welfare Flags'} labelAlt={lang === 'bn' ? 'Welfare Flags' : 'কল্যাণ সতর্কতা'} value={summary.welfare_flags || 0} icon={AlertTriangle} color="var(--color-warning)" />
      </div>

      {/* Report Period Tabs */}
      <div className="card-surface" style={{padding: 'var(--space-4)'}}>
        <div style={{display:'flex',gap:'var(--space-2)',flexWrap:'wrap'}}>
          {['Summary','Daily','Weekly','Monthly','Half-Yearly','Annual'].map(period => (
            <button key={period} className="btn btn-sm" style={{background:'var(--color-surface-2)',color:'var(--color-label-secondary)',fontSize:'var(--text-footnote)'}}>
              {lang === 'bn' ? ({'Summary':'সারসংক্ষেপ','Daily':'দৈনিক','Weekly':'সাপ্তাহিক','Monthly':'মাসিক','Half-Yearly':'অর্ধ-বার্ষিক','Annual':'বার্ষিক'}[period] || period) : period}
            </button>
          ))}
        </div>
      </div>

      {/* Export Controls */}
      <div style={{display:'flex',justifyContent:'flex-end'}}>
        <ExportBar title="Dashboard Report" data={schools} columns={[
          {label:'School',accessor:r => lang==='bn' ? r.name_bangla : (r.name_english||r.name_bangla)},
          {label:'District',accessor:'district'},{label:'Teachers',accessor:'total_teachers'},
          {label:'Students',accessor:'total_students'},{label:'Score',accessor:'composite_score'}
        ]} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-surface lg:col-span-2">
          <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-5)' }}>{lang === 'bn' ? 'উপস্থিতি সারসংক্ষেপ' : 'Attendance Overview'}</h3>
          <div className="flex items-center gap-8">
            <div style={{ width: 120, height: 120 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={attData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" startAngle={90} endAngle={-270}>
                    {attData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div style={{ fontSize: '40px', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--color-label-primary)' }}>{summary.attendance_rate || 0}%</div>
              <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'মাসিক উপস্থিতির হার' : 'Monthly Attendance Rate'}</p>
              <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-1)' }}>{summary.total_students || 0} {lang === 'bn' ? 'জন শিক্ষার্থী' : 'students across'} {summary.total_schools || 0} {lang === 'bn' ? 'টি বিদ্যালয়ে' : 'schools'}</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="card-surface">
          <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>{lang === 'bn' ? `সক্রিয় সতর্কতা (${alerts.length})` : `Open Alerts (${alerts.length})`}</h3>
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert, i) => (
              <div key={i} className="list-row" style={{ borderRadius: 'var(--radius-sm)', padding: 'var(--space-3) var(--space-4)' }}>
                <div className="list-row-icon" style={{ background: 'var(--color-warning-bg)' }}>
                  <AlertTriangle size={16} style={{ color: 'var(--color-warning)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p className="font-bangla" style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{alert.detail_bangla}</p>
                  <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{alert.school_name}</p>
                </div>
              </div>
            ))}
            {alerts.length === 0 && <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-tertiary)' }}>{lang === 'bn' ? 'কোনো সক্রিয় সতর্কতা নেই' : 'No open alerts'}</p>}
          </div>
        </div>
      </div>

      {/* School Rankings */}
      <div className="card-surface">
        <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-5)' }}>{lang === 'bn' ? 'বিদ্যালয় কর্মক্ষমতা র‍্যাংকিং' : 'School Performance Ranking'}</h3>
        <div className="overflow-x-auto">
          <table className="w-full data-table" data-testid="school-ranking-table">
            <thead>
              <tr>
                <th>#</th>
                <th>School (Bangla)</th>
                <th>{lang === 'bn' ? 'জেলা' : 'District'}</th>
                <th>{lang === 'bn' ? 'শিক্ষক' : 'Teachers'}</th>
                <th>{lang === 'bn' ? 'শিক্ষার্থী' : 'Students'}</th>
                <th>{lang === 'bn' ? 'স্কোর' : 'Score'}</th>
              </tr>
            </thead>
            <tbody>
              {schools.sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0)).map((s, i) => (
                <tr key={s.school_id}>
                  <td className="font-medium">{i + 1}</td>
                  <td className="font-bangla">{s.name_bangla}</td>
                  <td>{s.district}</td>
                  <td>{s.total_teachers}</td>
                  <td>{s.total_students}</td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${
                      s.composite_score >= 75 ? 'bg-emerald-50 text-emerald-700' :
                      s.composite_score >= 60 ? 'bg-amber-50 text-amber-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {s.composite_score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const lang = 'en'; // fallback for outside-context usage

export default function Dashboard() {
  const { isTeacher } = useApp();
  return isTeacher ? <TeacherDashboard /> : <AdminDashboard />;
}
